'use client';

/**
 * Quiz catalog page.
 *
 * Module: app/quizzes/page
 * Layer:  page (client component)
 *
 * Purpose:
 *     Lists the available quizzes as a filterable grid of cards. Provides a
 *     title search and a course filter (both client-side over the loaded
 *     catalog), and renders loading / error / empty states. Each card links to
 *     the quiz-taking page at `/quizzes/{id}`.
 *
 * AI context:
 *     - Entry points called by: Next.js App Router for `/quizzes`.
 *     - Depends on: use-quizzes (data + filters), quiz-grid (presentational
 *       pieces), Nav, useTranslation.
 *     - Side effects: none directly; the hook owns network I/O + cleanup.
 *     - Invariants: every visible string comes from the i18n catalogue; the
 *       page renders exactly one of loading / error / results.
 *     - Replacement cost: MEDIUM (primary quizzes surface).
 *
 * Threat model:
 *     - Quiz summaries are validated at the lib boundary before reaching here.
 */

import type React from 'react';
import Nav from '@/components/Nav';
import { useTranslation } from '@/lib/i18n';
import { ErrorPanel, LoadingPanel, Results, Toolbar } from './quiz-grid';
import { type QuizzesController, useQuizzes } from './use-quizzes';

/** Render the body region for the current controller state. */
function Body(props: { readonly ctrl: QuizzesController }): React.JSX.Element {
  const { ctrl } = props;
  if (ctrl.loading) {
    return <LoadingPanel />;
  }
  if (ctrl.errored) {
    return <ErrorPanel onRetry={ctrl.retry} />;
  }
  return (
    <>
      <Toolbar ctrl={ctrl} />
      <Results ctrl={ctrl} />
    </>
  );
}

/**
 * Render the quiz catalog page.
 *
 * @returns The catalog page element.
 */
export default function QuizzesPage(): React.JSX.Element {
  const { t } = useTranslation();
  const ctrl = useQuizzes();
  return (
    <div className='min-h-screen'>
      <Nav />
      <main className='mx-auto max-w-7xl px-4 py-8'>
        <div className='mb-8'>
          <h1 className='mb-2 text-3xl font-bold'>{t('quizzes.title')}</h1>
          <p className='opacity-70'>{t('quizzes.subtitle')}</p>
        </div>
        <Body ctrl={ctrl} />
      </main>
    </div>
  );
}
